<template>
  <v-card class="draw-popup-card" elevation="0">
    <v-card-text class="pa-2">
      <!-- Text（テキスト注釈）の場合 -->
      <template v-if="geometryType === 'Point' && isText">
        <!-- テキスト入力 -->
        <v-textarea
            v-track="'draw-popup-text-input'"
            ref="pointTextRef"
            v-model="localLabel"
            placeholder="テキストを入力"
            rows="2"
            auto-grow
            autofocus
            variant="outlined"
            density="compact"
            hide-details
            class="oh3-mb-1"
            @input="handleLabelInput"
        ></v-textarea>

        <!-- テキストカラーとフォントサイズ -->
        <div v-track="'draw-popup-text-color'" class="stylish-color-section oh3-mb-1">
          <div class="stylish-color-grid">
            <div
                v-for="color in polygonColorOptions"
                :key="color.value"
                class="stylish-color-item"
                :class="{
                  'is-selected': localTextColor === color.value,
                  'is-transparent': color.value === 'rgba(0,0,0,0)'
                }"
                @click="localTextColor = color.value; handleTextColorChange()"
            >
              <div
                  class="stylish-color-circle"
                  :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.value }"
              >
                <v-icon v-if="localTextColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
              </div>
            </div>
            <label class="stylish-color-item stylish-picker">
              <input
                type="color"
                v-model="localTextColor"
                @input="handleTextColorChange"
                class="draw-popup-hidden-input"
              />
              <div class="stylish-color-circle stylish-picker-circle">
                <v-icon size="14">mdi-eyedropper</v-icon>
              </div>
            </label>
          </div>
          <!-- フォントサイズ -->
          <div class="stylish-fontsize-row">
            <v-icon size="16" class="opacity-icon">mdi-format-size</v-icon>
            <v-slider
                v-model.number="localTextSize"
                min="10"
                max="100"
                step="1"
                hide-details
                density="compact"
                color="primary"
                class="stylish-fontsize-slider"
                @update:model-value="handleTextSizeChange"
            ></v-slider>
            <span class="stylish-fontsize-value">{{ localTextSize }}px</span>
          </div>
        </div>

        <!-- テキスト配置 -->
        <div class="d-flex align-center ga-2 oh3-mb-1">
          <span class="text-no-wrap popup-label oh3-text-base">配置</span>
          <v-btn-toggle
              v-track="'draw-popup-text-anchor'"
              v-model="localTextAnchor"
              mandatory
              color="primary"
              density="compact"
              class="flex-grow-1"
              @update:model-value="handleTextAnchorChange"
          >
            <v-btn value="left" size="small" class="flex-grow-1">
              <v-icon size="small">mdi-format-align-left</v-icon>
              <span class="oh3-ml-1">左</span>
            </v-btn>
            <v-btn value="center" size="small" class="flex-grow-1">
              <v-icon size="small">mdi-format-align-center</v-icon>
              <span class="oh3-ml-1">中央</span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- 削除・複製ボタン -->
        <v-row dense class="oh3-mb-1">
          <v-col cols="6">
            <v-btn
                v-track="'draw-popup-delete-text'"
                block
                size="small"
                color="error"
                class="popup-btn"
                @click="handleDelete"
            >
              <v-icon size="small" class="mr-1">mdi-delete</v-icon>
              削除
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
                v-track="'draw-popup-duplicate-text'"
                block
                size="small"
                color="primary"
                class="popup-btn"
                @click="handleDuplicate"
            >
              <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>
              複製
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <!-- Point（通常の点）の場合 -->
      <template v-else-if="geometryType === 'Point'">
        <!-- ラベル入力 -->
        <v-textarea
            ref="pointTextRef"
            v-model="localLabel"
            placeholder="ラベルに使用する短文を入力"
            rows="2"
            variant="outlined"
            density="compact"
            hide-details
            :id="featureId"
            class="oh3-mb-1"
            @input="handleLabelInput"
        ></v-textarea>

        <!-- 長文・画像・アイコン・スケッチボタン -->
        <div class="d-flex ga-1 oh3-mb-1">
          <v-btn
              v-track="'draw-popup-long-text'"
              size="small"
              color="primary"
              class="flex-grow-1 popup-btn"
              @click="props.onOpenLongText && props.onOpenLongText(featureId)"
          >
            長文
          </v-btn>
          <v-btn
              v-if="hasPhoto"
              v-track="'draw-popup-sketch'"
              size="small"
              color="primary"
              class="flex-grow-1 popup-btn"
              @click="props.onOpenSketch && props.onOpenSketch(featureId)"
          >
            <v-icon size="small" class="mr-1">mdi-draw</v-icon>
            スケッチ
          </v-btn>
          <v-btn
              v-track="'draw-popup-picture'"
              size="small"
              color="primary"
              class="flex-grow-1 popup-btn"
              @click="props.onOpenPicture && props.onOpenPicture(featureId)"
          >
            写真・PDF
          </v-btn>
          <v-btn
              v-track="'draw-popup-icon'"
              size="small"
              :color="showIconPicker ? 'secondary' : 'primary'"
              class="flex-grow-1 popup-btn"
              @click="showIconPicker = !showIconPicker"
          >
            <v-icon size="small" class="mr-1">mdi-sticker-emoji</v-icon>
            アイコン
          </v-btn>
        </div>

        <!-- アイコン選択パネル -->
        <div v-if="showIconPicker" class="icon-picker-panel oh3-mb-1">
          <!-- 最近使ったアイコン -->
          <div v-if="recentIcons.length > 0" class="recent-icons-section">
            <div class="recent-icons-label">最近（20件）</div>
            <div class="recent-icons-grid">
              <button
                v-for="icon in recentIcons"
                :key="icon.id"
                class="icon-grid-btn"
                :title="icon.label"
                @click="handleMdiIconSelect(icon.id)"
              >
                <svg class="icon-svg" viewBox="0 0 24 24">
                  <path fill="#333" :d="getMdiPath(icon.id)" />
                </svg>
              </button>
            </div>
          </div>
          <!-- カテゴリタブ -->
          <div class="icon-category-tabs">
            <button
              v-for="category in stampCategories"
              :key="category.id"
              :class="['icon-category-tab', { active: currentIconCategory === category.id }]"
              @click="currentIconCategory = category.id"
            >
              {{ category.name }}
            </button>
            <!-- 通常検索タブ -->
            <MiniTooltip text="7000+アイコンを英語で検索">
              <button
                :class="['icon-category-tab icon-category-tab-search', { active: currentIconCategory === 'mdi-search' }]"
                @click="currentIconCategory = 'mdi-search'"
              >
                <v-icon size="12">mdi-magnify</v-icon>
                検索
              </button>
            </MiniTooltip>
            <!-- AI検索タブ -->
            <MiniTooltip text="7000+アイコンを日本語で検索">
              <button
                :class="['icon-category-tab icon-category-tab-ai', { active: currentIconCategory === 'mdi-ai-search' }]"
                @click="currentIconCategory = 'mdi-ai-search'"
              >
                <v-icon size="12">mdi-robot</v-icon>
                AI検索
              </button>
            </MiniTooltip>
            <MiniTooltip text="MDI公式サイトで7000+アイコンを探す">
              <a
                href="https://pictogrammers.com/library/mdi/"
                target="_blank"
                class="mdi-link"
              >
                <v-icon size="12">mdi-open-in-new</v-icon>
              </a>
            </MiniTooltip>
          </div>

          <!-- 通常検索モード -->
          <template v-if="currentIconCategory === 'mdi-search'">
            <v-text-field
              v-model="mdiSearchQuery"
              placeholder="英語で検索 (例: star, home, car)"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-2"
              prepend-inner-icon="mdi-magnify"
              clearable
            />
            <div v-if="mdiSearchQuery && mdiSearchQuery.length < 2" class="text-caption text-grey mb-2">
              2文字以上入力してください
            </div>
            <div v-else-if="mdiSearchResults.length === 0 && mdiSearchQuery" class="text-caption text-grey mb-2">
              該当するアイコンが見つかりません
            </div>
            <div v-else-if="mdiSearchResults.length > 0" class="text-caption text-grey mb-1">
              {{ mdiSearchResults.length }}件のアイコン（最大50件表示）
            </div>
            <!-- MDI検索結果 -->
            <div class="icon-grid icon-grid-scrollable">
              <button
                v-for="iconName in mdiSearchResults"
                :key="iconName"
                class="icon-grid-btn"
                :title="iconName"
                @click="handleMdiIconSelect(iconName)"
              >
                <svg class="icon-svg" viewBox="0 0 24 24">
                  <path fill="#333" :d="getMdiPath(iconName)" />
                </svg>
              </button>
            </div>
          </template>

          <!-- AI検索モード -->
          <template v-else-if="currentIconCategory === 'mdi-ai-search'">
            <div class="d-flex ga-1 mb-2">
              <v-text-field
                v-model="aiSearchQuery"
                placeholder="日本語OK (例: 家, 車, 警告)"
                variant="outlined"
                density="compact"
                hide-details
                class="flex-grow-1"
                prepend-inner-icon="mdi-robot"
                :disabled="aiSearchLoading"
                @keydown.enter="handleAiSearchKeydown"
              />
              <v-btn
                color="primary"
                size="small"
                :loading="aiSearchLoading"
                :disabled="!aiSearchQuery || aiSearchLoading"
                @click="handleAiSearch"
              >
                検索
              </v-btn>
            </div>
            <div v-if="aiSearchLoading" class="text-caption text-grey mb-2">
              <v-progress-circular size="12" width="2" indeterminate class="mr-1"></v-progress-circular>
              AIがアイコンを探しています...
            </div>
            <div v-else-if="aiSearchError" class="text-caption text-red mb-2">
              {{ aiSearchError }}
            </div>
            <div v-else-if="aiSearchReason" class="text-caption text-grey mb-1">
              {{ aiSearchReason }}
            </div>
            <!-- AI検索結果 -->
            <div v-if="aiSearchResults.length > 0" class="icon-grid icon-grid-scrollable">
              <button
                v-for="iconName in aiSearchResults"
                :key="iconName"
                class="icon-grid-btn"
                :title="iconName"
                @click="handleMdiIconSelect(iconName)"
              >
                <svg class="icon-svg" viewBox="0 0 24 24">
                  <path fill="#333" :d="getMdiPath(iconName)" />
                </svg>
              </button>
            </div>
            <div v-else-if="!aiSearchLoading && !aiSearchError && aiSearchResults.length === 0" class="text-caption text-grey mb-2">
              日本語でアイコンを検索できます
            </div>
          </template>

          <!-- 通常カテゴリのアイコン一覧 -->
          <template v-else>
            <div class="icon-grid">
              <button
                v-for="stamp in currentCategoryStamps"
                :key="stamp.id"
                class="icon-grid-btn"
                :title="stamp.label"
                @click="handleIconSelect(stamp)"
              >
                <!-- 数字スタンプはテキスト表示 -->
                <template v-if="stamp.text">
                  <div class="icon-number" :style="{ color: stamp.color, borderColor: stamp.color }">
                    {{ stamp.text }}
                  </div>
                </template>
                <!-- 通常スタンプはSVG -->
                <template v-else>
                  <svg class="icon-svg" viewBox="0 0 24 24">
                    <path :fill="stamp.color" :d="stamp.path" />
                  </svg>
                </template>
              </button>
            </div>
          </template>

          <!-- アイコン解除ボタン -->
          <v-btn
            v-if="hasIcon"
            size="small"
            color="error"
            variant="outlined"
            class="mt-2"
            block
            @click="handleRemoveIcon"
          >
            <v-icon size="small" class="mr-1">mdi-close-circle</v-icon>
            アイコンを解除
          </v-btn>
        </div>

        <!-- スタイル選択 -->
        <div class="d-flex align-center ga-2 oh3-mb-1 oh3-text-base">
          <span class="text-no-wrap popup-label">吹き出し</span>
          <v-select
              v-model="localTextStyle"
              :items="textStyleOptions"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              class="flex-grow-1"
              @update:model-value="handleTextStyleChange"
          ></v-select>
        </div>

        <!-- 吹き出しの位置（吹き出し選択時のみ表示、上下のみ） -->
        <div v-if="localTextStyle === 1" class="oh3-mb-1">
          <div class="oh3-text-xs oh3-mb-1 oh3-text-secondary">吹き出しの位置:</div>
          <v-btn-toggle
              v-model="localBalloonDirection"
              mandatory
              color="primary"
              density="compact"
              class="w-100"
              @update:model-value="handleBalloonDirectionChange"
          >
            <v-btn
                v-track="'draw-popup-balloon-direction'"
                v-for="option in balloonDirectionOptions"
                :key="option.value"
                :value="option.value"
                size="small"
                class="flex-grow-1"
            >
              <v-icon size="small">{{ option.icon }}</v-icon>
              <span class="oh3-ml-1 balloon-btn-text">{{ option.title }}</span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- 削除・複製ボタン -->
        <v-row dense class="oh3-mb-1">
          <v-col cols="6">
            <v-btn
                v-track="'draw-popup-delete-point'"
                block
                size="small"
                color="error"
                class="popup-btn"
                @click="handleDelete"
            >
              <v-icon size="small" class="mr-1">mdi-delete</v-icon>
              削除
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
                v-track="'draw-popup-duplicate-point'"
                block
                size="small"
                color="primary"
                class="popup-btn"
                @click="handleDuplicate"
            >
              <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>
              複製
            </v-btn>
          </v-col>
        </v-row>

        <!-- スタイリッシュカラーセレクター -->
        <div class="stylish-color-section">
          <!-- テキストカラー -->
          <div class="stylish-color-label">テキスト色</div>
          <div class="stylish-color-grid">
            <div
                v-for="color in polygonColorOptions"
                :key="'text-' + color.value"
                class="stylish-color-item"
                :class="{
                  'is-selected': localTextColor === color.value,
                  'is-transparent': color.value === 'rgba(0,0,0,0)'
                }"
                @click="localTextColor = color.value; handleTextColorChange()"
            >
              <div
                  class="stylish-color-circle"
                  :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.value }"
              >
                <v-icon v-if="localTextColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
              </div>
            </div>
            <label class="stylish-color-item stylish-picker">
              <input
                type="color"
                v-model="localTextColor"
                @input="handleTextColorChange"
                class="draw-popup-hidden-input"
              />
              <div class="stylish-color-circle stylish-picker-circle">
                <v-icon size="14">mdi-eyedropper</v-icon>
              </div>
            </label>
          </div>
          <!-- フォントサイズ -->
          <div class="stylish-fontsize-row">
            <v-icon size="16" class="opacity-icon">mdi-format-size</v-icon>
            <v-slider
                v-model.number="localTextSize"
                min="10"
                max="100"
                step="1"
                hide-details
                density="compact"
                color="primary"
                class="stylish-fontsize-slider"
                @update:model-value="handleTextSizeChange"
            ></v-slider>
            <span class="stylish-fontsize-value">{{ localTextSize }}px</span>
          </div>
          <!-- ポイントカラー -->
          <div class="stylish-color-label" style="margin-top: var(--oh3-spacing-3);">ポイント色</div>
          <div class="stylish-color-grid">
            <div
                v-for="color in polygonColorOptions"
                :key="'point-' + color.value"
                class="stylish-color-item"
                :class="{
                  'is-selected': localPointColor === color.value,
                  'is-transparent': color.value === 'rgba(0,0,0,0)'
                }"
                @click="localPointColor = color.value; handlePointColorChange()"
            >
              <div
                  class="stylish-color-circle"
                  :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.value }"
              >
                <v-icon v-if="localPointColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
              </div>
            </div>
            <label class="stylish-color-item stylish-picker">
              <input
                type="color"
                v-model="localPointColor"
                @input="handlePointColorChange"
                class="draw-popup-hidden-input"
              />
              <div class="stylish-color-circle stylish-picker-circle">
                <v-icon size="14">mdi-eyedropper</v-icon>
              </div>
            </label>
          </div>
        </div>
      </template>

      <!-- LineString の場合 -->
      <template v-else-if="geometryType === 'LineString'">
        <!-- ラインストリングの場合のみ表示 -->
        <template v-if="!isFreeHand">
          <v-row dense class="mb-1">
            <v-col cols="6">
              <v-select
                  v-model="localArrowColor"
                  :items="arrowOptions"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:model-value="handleArrowChange"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                  v-model="localLabelPosition"
                  :items="labelPositionOptions"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:model-value="handleLabelPositionChange"
              ></v-select>
            </v-col>
          </v-row>

          <!-- 線幅スライダー（スタイリッシュ版） -->
          <div class="stylish-linewidth-row">
            <v-icon size="16" class="opacity-icon">mdi-pencil</v-icon>
            <v-slider
                v-model.number="localLineWidth"
                min="1"
                max="30"
                step="1"
                hide-details
                density="compact"
                color="primary"
                class="stylish-v-slider"
                @update:model-value="handleLineWidthChange"
            ></v-slider>
            <span class="stylish-linewidth-value">{{ localLineWidth }}px</span>
          </div>

          <v-row dense class="mb-1" align="center">
            <v-col cols="6">
              <v-select
                  v-model="localLineStyle"
                  :items="lineStyleOptions"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:model-value="handleLineStyleChange"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                  v-model="localCalc"
                  label="計測"
                  color="primary"
                  density="compact"
                  hide-details
                  @update:model-value="handleCalcChange"
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-textarea
              v-model="localLabel"
              placeholder="ラベル"
              rows="2"
              variant="outlined"
              density="compact"
              class="mb-1"
              hide-details
              @input="handleLabelInput"
          ></v-textarea>
        </template>

        <!-- フリーハンドの場合のみ表示 -->
        <template v-else>
          <!-- 線幅スライダー（スタイリッシュ版） -->
          <div class="stylish-linewidth-row">
            <v-icon size="16" class="opacity-icon">mdi-pencil</v-icon>
            <v-slider
                v-model.number="localLineWidth"
                min="1"
                max="30"
                step="1"
                hide-details
                density="compact"
                color="primary"
                class="stylish-v-slider"
                @update:model-value="handleLineWidthChange"
            ></v-slider>
            <span class="stylish-linewidth-value">{{ localLineWidth }}px</span>
          </div>

          <v-checkbox
              v-model="localKeiko"
              label="蛍光ペン"
              color="primary"
              density="compact"
              hide-details
              class="mb-1"
              @update:model-value="handleKeikoChange"
          ></v-checkbox>
        </template>

        <v-row dense class="mb-1">
          <v-col cols="4">
            <v-btn
                v-track="'draw-popup-delete-line'"
                block
                size="small"
                color="error"
                class="popup-btn"
                @click="handleDelete"
            >
              <v-icon size="small" class="mr-1">mdi-delete</v-icon>
              削除
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
                v-track="'draw-popup-duplicate-line'"
                block
                size="small"
                color="primary"
                class="popup-btn"
                @click="handleDuplicate"
            >
              <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>
              複製
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
                v-track="'draw-popup-line-output'"
                block
                size="small"
                class="popup-btn"
                color="primary"
                @click="handleLineOutput"
            >
              出力
            </v-btn>
          </v-col>
        </v-row>

        <!-- スタイリッシュカラーセレクター -->
        <div class="stylish-color-section">
          <div class="stylish-color-grid">
            <div
                v-for="color in polygonColorOptions"
                :key="color.value"
                class="stylish-color-item"
                :class="{
                  'is-selected': localLineColor === color.value,
                  'is-transparent': color.value === 'rgba(0,0,0,0)'
                }"
                @click="localLineColor = color.value; handleLineColorChange()"
            >
              <div
                  class="stylish-color-circle"
                  :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.value }"
              >
                <v-icon v-if="localLineColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
              </div>
            </div>
            <label class="stylish-color-item stylish-picker">
              <input
                type="color"
                v-model="customLineColor"
                @input="handleCustomLineColorChange"
                class="draw-popup-hidden-input"
              />
              <div class="stylish-color-circle stylish-picker-circle">
                <v-icon size="14">mdi-eyedropper</v-icon>
              </div>
            </label>
          </div>
        </div>
      </template>

      <!-- Polygon の場合 -->
      <template v-else-if="geometryType === 'Polygon'">
        <!-- 円（radiusプロパティあり）の場合 -->
        <template v-if="isCircle">
          <!-- 半径表示チェックと200mボタンを横並び -->
          <v-row dense class="mb-1">
            <v-col cols="6">
              <v-checkbox
                  v-model="localShowRadius"
                  label="半径表示"
                  color="primary"
                  density="compact"
                  hide-details
                  @update:model-value="handleShowRadiusChange"
              ></v-checkbox>
            </v-col>
            <v-col cols="6">
              <v-btn
                  v-track="'draw-popup-circle-200m'"
                  block
                  size="small"
                  color="primary"
                  variant="outlined"
                  style="margin-top: 6px"
                  @click="handleCircle200Click"
              >
                半径200m
              </v-btn>
            </v-col>
          </v-row>

          <!-- 半径スライダー（スタイリッシュ版） -->
          <div class="stylish-radius-row">
            <v-icon size="16" class="opacity-icon">mdi-radius-outline</v-icon>
            <v-slider
                v-model.number="localRadius"
                :min="10"
                :max="sliderMaxRadius"
                :step="10"
                hide-details
                density="compact"
                color="primary"
                class="stylish-v-slider"
                @update:model-value="handleRadiusChange"
            ></v-slider>
            <span class="stylish-radius-value">{{ localRadius }}m</span>
          </div>

          <v-text-field
              v-model.number="localRadius"
              type="number"
              min="1"
              max="100000"
              variant="outlined"
              density="compact"
              class="oh3-mb-1"
              hide-details
              @input="handleRadiusInputChange"
          ></v-text-field>

          <v-text-field
              v-model="localCircleText"
              placeholder="ここに入力"
              variant="outlined"
              density="compact"
              class="oh3-mb-1"
              hide-details
              @input="handleCircleTextChange"
          ></v-text-field>

          <!-- 円内人口計算 -->
          <div class="population-section mb-2">
            <v-btn
                v-if="!props.populationResult && !props.calculatingPopulation"
                v-track="'draw-popup-calculate-circle-population'"
                block
                size="small"
                color="teal"
                variant="tonal"
                @click="props.onCalculatePopulation && props.onCalculatePopulation(featureId)"
            >
              <v-icon size="small" class="mr-1">mdi-account-group</v-icon>
              円内人口
            </v-btn>
            <div v-else-if="props.calculatingPopulation" class="population-calculating">
              <v-progress-circular size="16" width="2" indeterminate color="teal" class="mr-2"></v-progress-circular>
              人口計算中...
            </div>
            <div v-else-if="props.populationResult" class="population-result-box">
              <div class="population-result-row">
                <v-icon size="small" color="teal" class="mr-1">mdi-account-group</v-icon>
                <span class="population-value">{{ formatPopulationDisplay(props.populationResult.population) }}</span>
                <span v-if="props.populationResult.agingRate > 0" class="aging-rate">
                  （高齢化率 {{ props.populationResult.agingRate }}%）
                </span>
                <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="teal"
                    class="ml-1"
                    title="再計算"
                    @click="props.onCalculatePopulation && props.onCalculatePopulation(featureId)"
                >
                  <v-icon size="small">mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="grey"
                    title="クリア"
                    @click="props.onClearPopulation && props.onClearPopulation(featureId)"
                >
                  <v-icon size="small">mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="population-note">({{ props.populationResult.meshSize }}メッシュ)</div>
            </div>
          </div>

          <v-row dense class="oh3-mb-1">
            <v-col cols="6">
              <v-btn
                  v-track="'draw-popup-delete-circle'"
                  block
                  size="small"
                  color="error"
                  class="popup-btn"
                  @click="handleDelete"
              >
                <v-icon size="small" class="mr-1">mdi-delete</v-icon>
                削除
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                  v-track="'draw-popup-duplicate-circle'"
                  block
                  size="small"
                  color="primary"
                  class="popup-btn"
                  @click="handleDuplicate"
              >
                <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>
                複製
              </v-btn>
            </v-col>
          </v-row>

          <!-- スタイリッシュカラーセレクター -->
          <div class="stylish-color-section">
            <div class="stylish-color-grid">
              <div
                  v-for="color in polygonColorOptions"
                  :key="color.value"
                  class="stylish-color-item"
                  :class="{
                    'is-selected': localPolygonColor === color.value,
                    'is-transparent': color.value === 'rgba(0,0,0,0)'
                  }"
                  @click="localPolygonColor = color.value; handlePolygonColorChange()"
              >
                <div
                    class="stylish-color-circle"
                    :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.value }"
                >
                  <v-icon v-if="localPolygonColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
                </div>
              </div>
              <label class="stylish-color-item stylish-picker">
                <input
                  type="color"
                  v-model="customPolygonColor"
                  @input="handleCustomPolygonColorChange"
                  class="draw-popup-hidden-input"
                />
                <div class="stylish-color-circle stylish-picker-circle">
                  <v-icon size="14">mdi-eyedropper</v-icon>
                </div>
              </label>
            </div>
            <!-- 透過度スライダー -->
            <div class="stylish-opacity-row">
              <v-icon size="16" class="opacity-icon">mdi-opacity</v-icon>
              <v-slider
                  v-model.number="localPolygonOpacity"
                  min="0"
                  max="100"
                  step="5"
                  hide-details
                  density="compact"
                  color="primary"
                  class="stylish-v-slider"
                  @update:model-value="handlePolygonOpacityChange"
              ></v-slider>
              <span class="stylish-opacity-value">{{ localPolygonOpacity }}%</span>
            </div>
          </div>
        </template>

        <!-- 通常のPolygonの場合 -->
        <template v-else>
          <v-checkbox
              v-model="localShowArea"
              label="面積表示"
              color="primary"
              density="compact"
              hide-details
              class="mb-1"
              @update:model-value="handleShowAreaChange"
          ></v-checkbox>

          <v-text-field
              v-model="localLabel"
              placeholder="ラベル"
              variant="outlined"
              density="compact"
              class="mb-1"
              hide-details
              @input="handleLabelInput"
          ></v-text-field>

          <!-- AI防災リスク診断ボタン（コメントアウト）
          <v-btn
              v-track="'draw-popup-area-analysis'"
              block
              size="small"
              color="primary"
              variant="tonal"
              class="mb-2"
              @click="handleAreaAnalysis"
          >
            <v-icon size="small" class="mr-1">mdi-chart-box-outline</v-icon>
              AI防災リスク診断
          </v-btn>
          -->

          <!-- ポリゴン内人口計算 -->
          <div class="population-section mb-2">
            <v-btn
                v-if="!props.populationResult && !props.calculatingPopulation"
                v-track="'draw-popup-calculate-population'"
                block
                size="small"
                color="teal"
                variant="tonal"
                @click="props.onCalculatePopulation && props.onCalculatePopulation(featureId)"
            >
              <v-icon size="small" class="mr-1">mdi-account-group</v-icon>
              ポリゴン内人口
            </v-btn>
            <div v-else-if="props.calculatingPopulation" class="population-calculating">
              <v-progress-circular size="16" width="2" indeterminate color="teal" class="mr-2"></v-progress-circular>
              人口計算中...
            </div>
            <div v-else-if="props.populationResult" class="population-result-box">
              <div class="population-result-row">
                <v-icon size="small" color="teal" class="mr-1">mdi-account-group</v-icon>
                <span class="population-value">{{ formatPopulationDisplay(props.populationResult.population) }}</span>
                <span v-if="props.populationResult.agingRate > 0" class="aging-rate">
                  （高齢化率 {{ props.populationResult.agingRate }}%）
                </span>
                <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="teal"
                    class="ml-1"
                    title="再計算"
                    @click="props.onCalculatePopulation && props.onCalculatePopulation(featureId)"
                >
                  <v-icon size="small">mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="grey"
                    title="クリア"
                    @click="props.onClearPopulation && props.onClearPopulation(featureId)"
                >
                  <v-icon size="small">mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="population-note">({{ props.populationResult.meshSize }}メッシュ)</div>
            </div>
          </div>

          <v-row dense class="mb-1">
            <v-col cols="4">
              <v-btn
                  v-track="'draw-popup-delete-polygon'"
                  block
                  size="small"
                  color="error"
                  class="popup-btn"
                  @click="handleDelete"
              >
                <v-icon size="small" class="mr-1">mdi-delete</v-icon>
                削除
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                  v-track="'draw-popup-delete-others'"
                  block
                  size="small"
                  color="error"
                  class="popup-btn"
                  @click="handleDeleteOthers"
              >
                <v-icon size="small" class="mr-1">mdi-delete-sweep</v-icon>
                他削除
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                  v-track="'draw-popup-duplicate-polygon'"
                  block
                  size="small"
                  color="primary"
                  class="popup-btn"
                  @click="handleDuplicate"
              >
                <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>
                複製
              </v-btn>
            </v-col>
          </v-row>

          <!-- スタイリッシュカラーセレクター -->
          <div class="stylish-color-section">
            <div class="stylish-color-grid">
              <div
                  v-for="color in polygonColorOptions"
                  :key="color.value"
                  class="stylish-color-item"
                  :class="{
                    'is-selected': localPolygonColor === color.value,
                    'is-transparent': color.value === 'rgba(0,0,0,0)'
                  }"
                  @click="localPolygonColor = color.value; handlePolygonColorChange()"
              >
                <div
                    class="stylish-color-circle"
                    :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.value }"
                >
                  <v-icon v-if="localPolygonColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
                </div>
              </div>
              <label class="stylish-color-item stylish-picker">
                <input
                  type="color"
                  v-model="customPolygonColor"
                  @input="handleCustomPolygonColorChange"
                  class="draw-popup-hidden-input"
                />
                <div class="stylish-color-circle stylish-picker-circle">
                  <v-icon size="14">mdi-eyedropper</v-icon>
                </div>
              </label>
            </div>
            <!-- 透過度スライダー -->
            <div class="stylish-opacity-row">
              <v-icon size="16" class="opacity-icon">mdi-opacity</v-icon>
              <v-slider
                  v-model.number="localPolygonOpacity"
                  min="0"
                  max="100"
                  step="5"
                  hide-details
                  density="compact"
                  color="primary"
                  class="stylish-v-slider"
                  @update:model-value="handlePolygonOpacityChange"
              ></v-slider>
              <span class="stylish-opacity-value">{{ localPolygonOpacity }}%</span>
            </div>
          </div>
        </template>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { updateFeatureProperties } from '@/js/drawUtils'
import { colorNameToRgba } from '@/js/pyramid'
import { isPdfFile } from '@/js/downLoad'
import { STAMP_CATEGORIES, getAllMdiIconNames, getMdiIconPath } from '@/js/stamps'
import MiniTooltip from '@/components/MiniTooltip.vue'

const store = useStore()
const pointTextRef = ref(null)

// アイコン選択関連
const showIconPicker = ref(false)
const currentIconCategory = ref('numbers')
const stampCategories = STAMP_CATEGORIES

// MDI検索関連
const mdiSearchQuery = ref('')
const allMdiIconNames = ref([])
// 非同期でMDIアイコン名をロード
getAllMdiIconNames().then(names => { allMdiIconNames.value = names })

// AI検索関連
const aiSearchQuery = ref('')
const aiSearchResults = ref([])
const aiSearchLoading = ref(false)
const aiSearchError = ref('')
const aiSearchReason = ref('')

// 最近使ったアイコン（ローカルストレージから読み込み）
const RECENT_ICONS_KEY = 'oh3.recentIcons'
const MAX_RECENT_ICONS = 20

const recentIcons = ref([])

// ローカルストレージから最近使ったアイコンを読み込み
const loadRecentIcons = () => {
  try {
    const stored = localStorage.getItem(RECENT_ICONS_KEY)
    if (stored) {
      recentIcons.value = JSON.parse(stored)
    }
  } catch (e) {
    console.warn('Failed to load recent icons:', e)
    recentIcons.value = []
  }
}

// 最近使ったアイコンを保存
const saveRecentIcon = (iconName, iconLabel) => {
  // 重複を除去（既存のものがあれば削除）
  const filtered = recentIcons.value.filter(icon => icon.id !== iconName)
  // 先頭に追加
  filtered.unshift({ id: iconName, label: iconLabel })
  // 最大件数を超えたら古いものを削除
  if (filtered.length > MAX_RECENT_ICONS) {
    filtered.pop()
  }
  recentIcons.value = filtered
  // ローカルストレージに保存
  try {
    localStorage.setItem(RECENT_ICONS_KEY, JSON.stringify(filtered))
  } catch (e) {
    console.warn('Failed to save recent icons:', e)
  }
}

// 初期化時に読み込み
loadRecentIcons()

// MDI検索結果（最大50件表示）
const mdiSearchResults = computed(() => {
  if (!mdiSearchQuery.value || mdiSearchQuery.value.length < 2) {
    return []
  }
  const query = mdiSearchQuery.value.toLowerCase()
  // mdiを除いて検索（例: 'mdiStar' → 'star'で検索可能）
  return allMdiIconNames.value
    .filter(name => {
      const searchName = name.slice(3).toLowerCase() // 'mdi'を除去
      return searchName.includes(query)
    })
    .slice(0, 50) // 最大50件
})

// 現在のカテゴリのスタンプ一覧
const currentCategoryStamps = computed(() => {
  if (currentIconCategory.value === 'mdi-search' || currentIconCategory.value === 'mdi-ai-search') {
    return [] // MDI検索・AI検索は別表示
  }
  const category = stampCategories.find(c => c.id === currentIconCategory.value)
  return category ? category.stamps : []
})

// 現在の点にアイコンがあるかどうか（propsの後で定義し直す）
// → propsの後に移動

const props = defineProps({
  featureId: {
    type: String,
    required: true
  },
  geometryType: {
    type: String,
    required: true,
    validator: (value) => ['Point', 'LineString', 'Polygon'].includes(value)
  },
  properties: {
    type: Object,
    required: true
  },
  // コールバック関数をpropsとして受け取る
  onClose: Function,
  onUpdateLabel: Function,
  onUpdateTextColor: Function,
  onUpdatePointColor: Function,
  onUpdateTextSize: Function,
  onUpdateTextStyle: Function,
  onUpdateBalloonDirection: Function,
  onUpdateOffset: Function,
  onUpdateLineColor: Function,
  onUpdateLineWidth: Function,
  onUpdateArrow: Function,
  onUpdateLineStyle: Function,
  onUpdateCalc: Function,
  onUpdateKeiko: Function,
  onUpdateLabelPosition: Function,
  onUpdatePolygonColor: Function,
  onUpdatePolygonOpacity: Function,
  onUpdateShowArea: Function,
  onDelete: Function,
  onDeleteOthers: Function,
  onDuplicate: Function,
  onOpenLongText: Function,
  onOpenPicture: Function,
  onOpenSketch: Function,
  onIconify: Function, // アイコン化コールバック
  onRemoveIcon: Function, // アイコン解除コールバック
  onLineOutput: Function,
  // 円用
  onUpdateCircleRadius: Function,
  onUpdateCircleShowRadius: Function,
  onUpdateCircleText: Function,
  // AI防災リスク診断
  onAreaAnalysis: Function,
  // ポリゴン内人口計算
  onCalculatePopulation: Function,
  onClearPopulation: Function,
  populationResult: Object,
  calculatingPopulation: Boolean
})

// フリーハンドかどうかの判定
const isFreeHand = computed(() => {
  return props.properties['free-hand'] || false
})

// 円かどうかの判定（radiusプロパティがあるかで判定）
const isCircle = computed(() => {
  return props.geometryType === 'Polygon' && props.properties.radius !== undefined
})

// テキストかどうかの判定（type='Text'で判定）
const isText = computed(() => {
  return props.geometryType === 'Point' && props.properties.type === 'Text'
})

// ローカルステート
const localLabel = ref(props.properties.label || '')
const localTextColor = ref(props.properties.color || 'black')
// スタンプの場合はstampColorを使用、通常の点はpoint-colorを使用
const localPointColor = ref(
  props.properties.stamp === true
    ? (props.properties.stampColor || 'black')
    : (props.properties['point-color'] || 'black')
)
const localTextSize = ref(props.properties['text-size'] || 20)
// テキストの配置（left or center）
const localTextAnchor = ref(props.properties.textAnchor || 'left')
// labelTypeはPointの場合、0または'0'のみ通常、それ以外は全て吹き出し(1)
// PDFの場合は強制的に通常(0)にする
const hasPdf = isPdfFile(props.properties.pictureUrl?.pictureUrl || props.properties.pictureUrl || '')
const localTextStyle = ref(
  hasPdf ? 0 : (props.properties.labelType === 0 || props.properties.labelType === '0' ? 0 : 1)
)
// balloonDirectionは上下のみ対応（左右は可変幅マーカーのため非対応）
const localBalloonDirection = ref(hasPdf ? 'top' : (props.properties.balloonDirection || 'top'))
const localOffsetX = ref(props.properties.offsetValue?.[0] || 0.6)
const localOffsetY = ref(props.properties.offsetValue?.[1] || 0)
const localLineColor = ref(props.properties.color || 'black')
const localLineWidth = ref(props.properties['line-width'] || 5)
const localArrowColor = ref(props.properties['arrow-type'] || props.properties.arrow || 'end')
const localLineStyle = ref(props.properties['line-style'] || 'solid')
const localLabelPosition = ref(props.properties.labelType || props.properties.labelPosition || 'start')
const localCalc = ref(Boolean(props.properties.calc))
const localKeiko = ref(Boolean(props.properties.keiko))
const localPolygonColor = ref(props.properties.colorName || 'blue')
const localShowArea = ref(props.properties.isArea || false)
const customPolygonColor = ref('#0000ff')
const customLineColor = ref('#000000')
// 透過度（0-100、100が完全不透明）- ポリゴンのみ
const localPolygonOpacity = ref(Math.round((props.properties['fill-opacity'] ?? 0.5) * 100))

// 円用のローカルステート
const localRadius = ref(props.properties.radius || 100)
// スライダーの上限を初期半径を基準に固定（動的に変わらないようにする）
const sliderMaxRadius = ref(Math.max(1000, Math.round((props.properties.radius || 100) * 5)))
const localShowRadius = ref(props.properties.showRadius !== false)  // showRadiusプロパティを使用（デフォルトtrue）
const localCircleText = ref(props.properties.label2 || '')

// オプション
const colorOptions = [
  { label: '赤', value: 'red' },
  { label: '黒', value: 'black' },
  { label: '青', value: 'blue' },
  { label: '緑', value: 'green' },
  { label: 'オレンジ', value: 'orange' },
  { label: 'ピンク', value: 'hotpink' }
]

const pointColorOptions = [
  ...colorOptions,
  { label: '透明', value: 'rgba(0,0,0,0)' }
]

const textStyleOptions = [
  { title: '通常', value: 0 },
  { title: '吹き出し', value: 1 }
]

const balloonDirectionOptions = [
  { title: '上', value: 'top', icon: 'mdi-arrow-up' },
  { title: '下', value: 'bottom', icon: 'mdi-arrow-down' }
]

const arrowOptions = [
  { title: '矢印有り', value: 'end' },
  { title: '矢印無し', value: 'none' },
  { title: '両矢印', value: 'both' }
]

const labelPositionOptions = [
  { title: 'ラベル始め', value: 'start' },
  { title: 'ラベル中', value: 'mid' },
  { title: 'ラベルなし', value: 'none' }
]

const lineStyleOptions = [
  { title: '実線', value: 'solid' },
  { title: '破線', value: 'dashed' },
  { title: '点線', value: 'dotted' }
]

const lineColorOptions = [
  { label: '赤', value: 'red' },
  { label: '黒', value: 'black' },
  { label: '青', value: 'blue' },
  { label: '緑', value: 'green' },
  { label: 'オレンジ', value: 'orange' },
  { label: 'ピンク', value: 'hotpink' }
]

const polygonColorOptions = [
  { label: '赤', value: 'red' },
  { label: '黒', value: 'black' },
  { label: '青', value: 'blue' },
  { label: '緑', value: 'green' },
  { label: 'オレンジ', value: 'orange' },
  { label: 'ピンク', value: 'hotpink' },
  { label: '黄', value: 'yellow' },
  { label: '透明', value: 'rgba(0,0,0,0)' }
]

// イベントハンドラ
const handleLabelInput = () => {
  // HTML版と同じくisLabelUpdatedをtrueにする
  store.state.isLabelUpdated = true
  if (props.onUpdateLabel) props.onUpdateLabel(props.featureId, localLabel.value)
  if (props.geometryType === 'LineString' && !isFreeHand.value) {
    // ラインのラベル変更も次回に引き継ぐ
    saveLastLineStyle()
  } else if (isText.value) {
    // テキスト注釈のラベル変更
    saveLastTextStyle()
  } else if (props.geometryType === 'Point') {
    // 点のラベル変更も引き継ぎ
    saveLastPointStyle()
  }
}

const handleTextColorChange = () => {
  if (props.onUpdateTextColor) props.onUpdateTextColor(props.featureId, localTextColor.value)
  // スタイルを保存
  if (isText.value) {
    saveLastTextStyle()
  } else if (props.geometryType === 'Point') {
    saveLastPointStyle()
  }
}

const handlePointColorChange = () => {
  if (props.onUpdatePointColor) props.onUpdatePointColor(props.featureId, localPointColor.value)
  // 前回のスタイルを保存（次の地物作成時に引き継ぐ）
  saveLastPointStyle()
}

const handleTextSizeChange = () => {
  if (props.onUpdateTextSize) props.onUpdateTextSize(props.featureId, localTextSize.value)
  // 前回のスタイルを保存
  if (isText.value) {
    saveLastTextStyle()
  } else {
    saveLastPointStyle()
  }
}

const handleTextAnchorChange = () => {
  // textAnchorとtextJustifyの両方を更新
  // text-anchor: テキストボックスの基準点（アンカー）の位置
  // text-justify: 複数行テキストの行揃え
  updateFeatureProperties(props.featureId, {
    textAnchor: localTextAnchor.value,
    textJustify: localTextAnchor.value  // left or center
  })
  // テキスト注釈のスタイルを保存
  saveLastTextStyle()
}

const handleTextStyleChange = () => {
  // 写真マーカーで吹き出しを「通常(0)」に変更したときは、labelTypeとballoonDirectionを同時に更新
  if (localTextStyle.value === 0 && props.properties.pictureUrl) {
    localBalloonDirection.value = 'top'
    // 複数プロパティを一度に更新してマーカー再作成を1回だけにする
    updateFeatureProperties(props.featureId, {
      labelType: String(localTextStyle.value),
      balloonDirection: 'top'
    })
  } else {
    if (props.onUpdateTextStyle) props.onUpdateTextStyle(props.featureId, localTextStyle.value)
  }
  if (props.geometryType === 'Point') saveLastPointStyle()
}

const handleBalloonDirectionChange = () => {
  if (props.onUpdateBalloonDirection) props.onUpdateBalloonDirection(props.featureId, localBalloonDirection.value)
  if (props.geometryType === 'Point') saveLastPointStyle()
}

const handleOffsetChange = () => {
  if (props.onUpdateOffset) props.onUpdateOffset(props.featureId, [localOffsetX.value, localOffsetY.value])
}

const handleLineColorChange = () => {
  if (props.onUpdateLineColor) props.onUpdateLineColor(props.featureId, localLineColor.value)
  // 前回のスタイルを保存（次の地物作成時に引き継ぐ）
  saveLastLineStyle()
}

const handleLineWidthChange = () => {
  if (props.onUpdateLineWidth) props.onUpdateLineWidth(props.featureId, localLineWidth.value)
  // 前回のスタイルを保存
  saveLastLineStyle()
}

const handleArrowChange = () => {
  if (props.onUpdateArrow) props.onUpdateArrow(props.featureId, localArrowColor.value)
  // 前回のスタイルを保存
  saveLastLineStyle()
}

const handleLineStyleChange = () => {
  if (props.onUpdateLineStyle) props.onUpdateLineStyle(props.featureId, localLineStyle.value)
  saveLastLineStyle()
}

const handleCalcChange = () => {
  if (props.onUpdateCalc) props.onUpdateCalc(props.featureId, localCalc.value)
  saveLastLineStyle()
}

const handleLabelPositionChange = () => {
  if (props.onUpdateLabelPosition) props.onUpdateLabelPosition(props.featureId, localLabelPosition.value)
  saveLastLineStyle()
}

const handleLineOutput = () => {
  if (props.onLineOutput) props.onLineOutput(props.featureId)
}

const handlePolygonColorChange = () => {
  if (props.onUpdatePolygonColor) props.onUpdatePolygonColor(props.featureId, localPolygonColor.value)
  // 前回のスタイルを保存（次の地物作成時に引き継ぐ）
  saveLastPolygonStyle()
}

const handlePolygonOpacityChange = () => {
  if (props.onUpdatePolygonOpacity) props.onUpdatePolygonOpacity(props.featureId, localPolygonOpacity.value / 100)
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

const handleShowAreaChange = () => {
  if (props.onUpdateShowArea) props.onUpdateShowArea(props.featureId, localShowArea.value)
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

const handleDelete = () => {
  if (props.onDelete) props.onDelete(props.featureId)
}

const handleDeleteOthers = () => {
  if (props.onDeleteOthers) props.onDeleteOthers(props.featureId)
}

const handleDuplicate = () => {
  if (props.onDuplicate) props.onDuplicate(props.featureId)
}

const handleAreaAnalysis = () => {
  if (props.onAreaAnalysis) props.onAreaAnalysis(props.featureId)
}

// アイコン選択ハンドラー
const handleIconSelect = (stamp) => {
  // パネルは閉じない（通常の点と同じ動作）
  if (props.onIconify) {
    props.onIconify(props.featureId, stamp)
    // 最近使ったアイコンに追加（MDIアイコンの場合のみ、カテゴリ内は除外）
    if (stamp.mdiIcon) {
      saveRecentIcon(stamp.mdiIcon, stamp.label)
    } else if (stamp.id && stamp.id.startsWith('mdi')) {
      saveRecentIcon(stamp.id, stamp.label)
    }
  }
}

// MDIアイコンパスを取得（テンプレート用）
const getMdiPath = (iconName) => {
  return getMdiIconPath(iconName) || ''
}

// MDIアイコン選択ハンドラー（検索結果から選択）
const handleMdiIconSelect = (iconName) => {
  // MDIアイコン名からスタンプオブジェクトを作成
  const label = iconName.replace(/^mdi/, '') // 例: 'Star'
  const stamp = {
    id: iconName, // 例: 'mdiStar'
    label: label,
    color: '#333333',
    path: getMdiIconPath(iconName)
  }
  if (props.onIconify) {
    props.onIconify(props.featureId, stamp)
    // 最近使ったアイコンに追加
    saveRecentIcon(iconName, label)
  }
}

// AI検索キー入力ハンドラー（IME変換中は無視）
const handleAiSearchKeydown = (e) => {
  if (e.isComposing) return // IME変換中のEnterは無視
  handleAiSearch()
}

// AI検索ハンドラー
const handleAiSearch = async () => {
  if (!aiSearchQuery.value || aiSearchLoading.value) return

  aiSearchLoading.value = true
  aiSearchError.value = ''
  aiSearchReason.value = ''
  aiSearchResults.value = []

  try {
    const response = await fetch('/oh3/php/claudeIconSearch.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query: aiSearchQuery.value })
    })

    const data = await response.json()

    if (data.success && data.icons) {
      // 有効なアイコンのみフィルタリング（実際に@mdi/jsに存在するもの）
      const validIcons = data.icons.filter(iconName => getMdiIconPath(iconName))
      aiSearchResults.value = validIcons
      aiSearchReason.value = data.reason || `${validIcons.length}件のアイコンが見つかりました`
    } else {
      aiSearchError.value = data.error || 'AI検索に失敗しました'
    }
  } catch (error) {
    console.error('AI search error:', error)
    aiSearchError.value = 'AI検索でエラーが発生しました'
  } finally {
    aiSearchLoading.value = false
  }
}

// 現在の点にアイコンがあるかどうか
const hasIcon = computed(() => {
  return props.properties?.stamp === true
})

// 写真が添付されているか（スケッチボタン表示判定用）
const hasPhoto = computed(() => {
  return !!props.properties?.pictureUrl && !!props.properties?.photoMemoId
})

// アイコン解除ハンドラー
const handleRemoveIcon = () => {
  if (props.onRemoveIcon) {
    props.onRemoveIcon(props.featureId)
  }
}

// 人口表示フォーマット
const formatPopulationDisplay = (population) => {
  if (population === 0) {
    return '0人'
  }
  if (population < 1000) {
    return `約${population}人`
  } else if (population < 10000) {
    return `約${(population / 1000).toFixed(1)}千人`
  } else if (population < 100000000) {
    return `約${Math.round(population / 10000)}万人`
  } else {
    return `約${(population / 100000000).toFixed(1)}億人`
  }
}

// スタイル保存関数（次の地物作成時に引き継ぐため）
const saveLastLineStyle = () => {
  if (isFreeHand.value) {
    // フリーハンドの場合
    store.state.lastFreeHandStyle = {
      color: localLineColor.value,
      'keiko-color': props.properties['keiko-color'],
      'line-width': localLineWidth.value,
      label: localLabel.value,
      keiko: localKeiko.value ? 1 : 0,
    }
  } else {
    // 通常ラインの場合
    store.state.lastLineStyle = {
      color: localLineColor.value,
      colorName: localLineColor.value,
      'line-width': localLineWidth.value,
      'line-style': localLineStyle.value,
      label: localLabel.value,
      arrowType: localArrowColor.value,
      arrowColor: localArrowColor.value,
      labelType: localLabelPosition.value,
      calc: localCalc.value ? 1 : 0,
      keiko: localKeiko.value ? 1 : 0,
    }
  }
}

const saveLastPolygonStyle = () => {
  // localPolygonColorから色を生成（props.properties.colorは非同期更新のためタイミングで古い値の可能性あり）
  const colorFromName = colorNameToRgba(localPolygonColor.value, localPolygonOpacity.value / 100)
  if (isCircle.value) {
    // 円の場合
    store.state.lastCircleStyle = {
      color: colorFromName,
      colorName: localPolygonColor.value,
      'line-width': props.properties['line-width'],
      showRadius: localShowRadius.value,
      label2: localCircleText.value,
      opacity: localPolygonOpacity.value / 100,
    }
  } else {
    // 通常ポリゴンの場合
    store.state.lastPolygonStyle = {
      color: colorFromName,
      colorName: localPolygonColor.value,
      'line-width': props.properties['line-width'],
      label: localLabel.value,
      opacity: localPolygonOpacity.value / 100,
      isArea: localShowArea.value,
    }
  }
}

const saveLastPointStyle = () => {
  const last = {
    color: localTextColor.value,
    'point-color': localPointColor.value,
    label: localLabel.value,
    'text-size': localTextSize.value,
    labelType: String(localTextStyle.value),
    balloonDirection: localBalloonDirection.value,
  };
  store.state.lastPointStyle = last;
  // 次回作成時のフォールバック用に current* も更新
  store.state.currentTextColor = last.color;
  store.state.currentPointColor = last['point-color'];
  store.state.currentTextSize = last['text-size'];
  store.state.currentTextLabelType = last.labelType;
}

// テキスト注釈用のスタイル保存
const saveLastTextStyle = () => {
  const last = {
    color: localTextColor.value,
    label: localLabel.value,
    'text-size': localTextSize.value,
    textAnchor: localTextAnchor.value,
  };
  store.state.lastTextStyle = last;
}

const handleKeikoChange = () => {
  if (props.onUpdateKeiko) props.onUpdateKeiko(props.featureId, localKeiko.value)
  saveLastLineStyle()
}

// ライン用カスタムカラー処理
const handleCustomLineColorChange = () => {
  localLineColor.value = customLineColor.value
  if (props.onUpdateLineColor) props.onUpdateLineColor(props.featureId, customLineColor.value)
  // 前回のスタイルを保存
  saveLastLineStyle()
}

// ポリゴン用カスタムカラー処理
const handleCustomPolygonColorChange = () => {
  localPolygonColor.value = customPolygonColor.value
  if (props.onUpdatePolygonColor) props.onUpdatePolygonColor(props.featureId, customPolygonColor.value)
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

// 円用ハンドラー
const handleShowRadiusChange = () => {
  if (props.onUpdateCircleShowRadius) {
    props.onUpdateCircleShowRadius(props.featureId, localShowRadius.value)
  }
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

const handleRadiusChange = () => {
  if (props.onUpdateCircleRadius) {
    props.onUpdateCircleRadius(props.featureId, localRadius.value, localCircleText.value)
  }
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

const handleRadiusInputChange = () => {
  if (props.onUpdateCircleRadius) {
    props.onUpdateCircleRadius(props.featureId, localRadius.value, localCircleText.value)
  }
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

// ボタンクリックで半径を200mに設定
const handleCircle200Click = () => {
  localRadius.value = 200
  if (props.onUpdateCircleRadius) {
    props.onUpdateCircleRadius(props.featureId, 200, localCircleText.value)
  }
  saveLastPolygonStyle()
}

const handleCircleTextChange = () => {
  if (props.onUpdateCircleText) {
    props.onUpdateCircleText(props.featureId, localRadius.value, localCircleText.value)
  }
  // 前回のスタイルを保存
  saveLastPolygonStyle()
}

// チェックマークの色を決定（黄色と透明は濃い色）
const getCheckColor = (bgColor) => {
  if (bgColor === 'yellow' || bgColor === 'rgba(0,0,0,0)') {
    return 'grey-darken-2'
  }
  return 'white'
}

// propsの変更を監視してローカルステートを更新
watch(() => props.properties, (newProps) => {
  localLabel.value = newProps.label || ''
  localTextColor.value = newProps.color || 'black'
  // スタンプの場合はstampColorを使用、通常の点はpoint-colorを使用
  localPointColor.value = newProps.stamp === true
    ? (newProps.stampColor || 'black')
    : (newProps['point-color'] || 'black')
  localTextSize.value = newProps['text-size'] || 20
  // labelTypeはPointの場合、0または'0'のみ通常、それ以外は全て吹き出し(1)
  localTextStyle.value = newProps.labelType === 0 || newProps.labelType === '0' ? 0 : 1
  localBalloonDirection.value = newProps.balloonDirection || 'top'
  localOffsetX.value = newProps.offsetValue?.[0] || 0.6
  localOffsetY.value = newProps.offsetValue?.[1] || 0
  localLineColor.value = newProps.color || 'black'
  localLineWidth.value = newProps['line-width'] || 5
  localArrowColor.value = newProps['arrow-type'] || newProps.arrow || 'end'
  localLineStyle.value = newProps['line-style'] || 'solid'
  localLabelPosition.value = newProps.labelType || newProps.labelPosition || 'start'
  localCalc.value = Boolean(newProps.calc)
  localKeiko.value = Boolean(newProps.keiko)
  localPolygonColor.value = newProps.colorName || 'blue'
  localShowArea.value = newProps.isArea || false
  // 透過度（ポリゴンのみ）
  localPolygonOpacity.value = Math.round((newProps['fill-opacity'] ?? 0.5) * 100)
  // 円用プロパティ
  localRadius.value = newProps.radius || 100
  localShowRadius.value = newProps.showRadius !== false  // showRadiusプロパティを使用（デフォルトtrue）
  localCircleText.value = newProps.label2 || ''
}, { deep: true })

// featureIdが変わった時（別の円を選択した時）にスライダー上限を再計算
watch(() => props.featureId, () => {
  const radius = props.properties.radius || 100
  sliderMaxRadius.value = Math.max(1000, Math.round(radius * 5))
})

// マウント時にtextarea要素にpoint-textクラスを追加（pyramid.jsのlavelUpdateが使用）
onMounted(() => {
  nextTick(() => {
    if (pointTextRef.value && props.geometryType === 'Point') {
      // Vuetifyのv-textareaは内部的にtextarea要素を持つので、それを探す
      const textarea = pointTextRef.value.$el?.querySelector('textarea')
      if (textarea) {
        textarea.classList.add('point-text')
        textarea.id = props.featureId
      }
    }

    // PDFの場合、吹き出しを強制的に通常(0)に設定してプロパティも更新
    if (hasPdf && props.geometryType === 'Point') {
      const currentLabelType = props.properties.labelType
      if (currentLabelType !== 0 && currentLabelType !== '0') {
        updateFeatureProperties(props.featureId, {
          labelType: '0',
          balloonDirection: 'top'
        })
      }
    }
  })
})
</script>

<style scoped>
.draw-popup-card {
  width: 100%;
  height: 100%;
  box-shadow: none !important;
}

.draw-popup-card :deep(.v-card-text) {
  padding: 6px !important;
  max-height: 70vh;
  overflow-y: auto;
}

.text-caption {
  font-size: 0.75rem;
  color: var(--oh3-color-overlay-60);
}

.popup-btn {
  height: 30px;
}

.popup-label {
  min-width: 60px;
}

.balloon-btn-text {
  font-size: 11px;
}

.text-size-input {
  width: 60px;
}

/* Design system-based utility classes */
.draw-popup-check-icon {
  position: absolute;
  top: var(--oh3-spacing-1);
  right: var(--oh3-spacing-1);
}

.draw-popup-hidden-input {
  opacity: 0;
  position: absolute;
  pointer-events: none;
}

.draw-popup-color-bg {
  /* Placeholder for dynamic backgroundColor binding */
}

.draw-popup-transparent-bg {
  border: 1px solid var(--oh3-color-border-default);
  background: linear-gradient(45deg, #ccc 25%, transparent 25%),
              linear-gradient(-45deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(-45deg, transparent 75%, #ccc 75%) !important;
  background-size: 8px 8px !important;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0px !important;
}

.draw-popup-label-dark {
  color: var(--oh3-color-text-secondary) !important;
}

.radius-display {
  font-size: var(--oh3-font-size-xl);
}

.color-selector {
  display: flex;
  gap: 0;
  border: 1px solid var(--oh3-color-border-dark);
}

.color-box {
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.color-box:hover {
  opacity: 0.8;
}

/* ポイント用のコンパクトな色ボックス */
.color-box-compact {
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  position: relative;
}

.color-box-compact:hover {
  opacity: 0.8;
}

.color-label {
  font-size: var(--oh3-font-size-base);
  font-weight: bold;
  color: white;
  text-shadow: 0 0 2px var(--oh3-color-overlay-50);
}

.color-picker-box {
  background: linear-gradient(135deg, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%);
  position: relative;
}

/* 透過度スライダー */
.opacity-slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: var(--oh3-spacing-2);
}

.opacity-label {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-overlay-60);
  min-width: 28px;
}

.opacity-slider {
  flex: 1;
  height: 4px;
  cursor: pointer;
}

.opacity-value {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-overlay-80);
  min-width: 36px;
  text-align: right;
}

/* スタイリッシュカラーセレクター */
.stylish-color-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: var(--oh3-radius-xl);
  padding: var(--oh3-spacing-3);
  margin-bottom: var(--oh3-spacing-2);
}

.stylish-color-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--oh3-color-overlay-60);
  margin-bottom: 6px;
  text-align: center;
}

.stylish-color-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.stylish-color-item {
  position: relative;
  cursor: pointer;
  padding: 3px;
  border-radius: var(--oh3-radius-full);
  transition: all 0.2s ease;
}

.stylish-color-item:hover {
  transform: scale(1.15);
}

.stylish-color-item.is-selected {
  padding: 2px;
  border: 2px solid var(--oh3-color-link);
  box-shadow: 0 0 0 2px var(--oh3-color-primary-alpha-30);
}

.stylish-color-circle {
  width: 26px;
  height: 26px;
  border-radius: var(--oh3-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px var(--oh3-color-overlay-15), inset 0 1px 2px var(--oh3-color-overlay-white-70);
  transition: all 0.2s ease;
}

.stylish-color-item.is-transparent .stylish-color-circle {
  background: linear-gradient(45deg, #ccc 25%, transparent 25%),
              linear-gradient(-45deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
  border: 1px dashed var(--oh3-color-gray-500);
}

.stylish-picker-circle {
  background: conic-gradient(
    from 0deg,
    #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000
  );
}

.stylish-picker .stylish-color-circle {
  position: relative;
}

.stylish-picker .stylish-color-circle::after {
  content: '';
  position: absolute;
  inset: 4px;
  background: white;
  border-radius: var(--oh3-radius-full);
}

.stylish-picker .stylish-color-circle .v-icon {
  position: relative;
  z-index: 1;
}

/* 透過度スライダー */
.stylish-opacity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--oh3-color-overlay-10);
}

.opacity-icon {
  color: var(--oh3-color-overlay-50);
}

.stylish-slider-wrap {
  flex: 1;
  position: relative;
}

.stylish-opacity-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(to right, var(--oh3-color-overlay-10), rgba(0,0,0,0.4));
  appearance: none;
  cursor: pointer;
}

.stylish-opacity-slider::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: var(--oh3-radius-full);
  background: white;
  border: 2px solid var(--oh3-color-link);
  box-shadow: 0 2px 4px var(--oh3-color-overlay-20);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.stylish-opacity-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.stylish-opacity-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: var(--oh3-radius-full);
  background: white;
  border: 2px solid var(--oh3-color-link);
  box-shadow: 0 2px 4px var(--oh3-color-overlay-20);
  cursor: pointer;
}

.stylish-opacity-value {
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  color: var(--oh3-color-link);
  min-width: 36px;
  text-align: right;
}

/* 半径スライダー（円用） */
.stylish-radius-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--oh3-spacing-2);
}

.stylish-radius-value {
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  color: var(--oh3-color-link);
  min-width: 52px;
  text-align: right;
}

/* 線幅スライダー（線用） */
.stylish-linewidth-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--oh3-spacing-2);
}

.stylish-linewidth-value {
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  color: var(--oh3-color-link);
  min-width: 36px;
  text-align: right;
}

/* v-slider共通スタイル */
.stylish-v-slider {
  flex: 1;
}

/* フォントサイズスライダー */
.stylish-fontsize-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--oh3-color-overlay-10);
}

.stylish-fontsize-slider {
  flex: 1;
}

.stylish-fontsize-value {
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  color: var(--oh3-color-link);
  min-width: 40px;
  text-align: right;
}

/* テキストFW用スタイル */
.stylish-text-section {
  padding: 8px 10px;
}

.stylish-text-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stylish-color-grid-text {
  flex: 1;
  gap: 4px;
}

.stylish-color-item-sm {
  padding: 2px;
}

.stylish-color-item-sm.is-selected {
  padding: 1px;
  border-width: 1.5px;
}

.stylish-color-circle-sm {
  width: 20px;
  height: 20px;
}

.text-size-input-stylish {
  max-width: 75px;
  flex-shrink: 0;
}

.text-size-input-stylish :deep(.v-field__input) {
  min-height: 32px;
  padding: 4px 8px;
}

/* 小画面（500px以下）でボタン・入力欄を大きく */
@media (max-width: 500px) {
  .popup-btn {
    height: 60px !important;
    font-size: var(--oh3-font-size-md);
  }

  :deep(.v-textarea .v-field__input) {
    min-height: 80px !important;
    font-size: var(--oh3-font-size-md);
  }

  :deep(.v-text-field .v-field__input) {
    min-height: 56px !important;
    font-size: var(--oh3-font-size-md);
  }

  :deep(.v-field) {
    font-size: var(--oh3-font-size-md);
  }
}

/* ポリゴン内人口計算 */
.population-section {
  margin-top: var(--oh3-spacing-1);
}

.population-calculating {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: var(--oh3-color-surface-alt, #f5f5f5);
  border-radius: var(--oh3-radius-sm, 4px);
  font-size: var(--oh3-font-size-sm, 13px);
  color: var(--oh3-color-text-secondary, #666);
}

.population-result-box {
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.1), rgba(0, 150, 136, 0.05));
  border: 1px solid rgba(0, 150, 136, 0.3);
  border-radius: var(--oh3-radius-sm, 4px);
}

.population-result-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}

.population-value {
  font-size: var(--oh3-font-size-base, 14px);
  font-weight: 600;
  color: #00796b;
}

.aging-rate {
  font-size: var(--oh3-font-size-sm, 13px);
  color: #d84315;
  font-weight: 500;
}

.population-note {
  font-size: var(--oh3-font-size-xs, 11px);
  color: var(--oh3-color-text-secondary, #666);
  margin-top: 2px;
}

/* アイコン選択パネル */
.icon-picker-panel {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: var(--oh3-radius-md);
  padding: var(--oh3-spacing-2);
  border: 1px solid var(--oh3-color-border-default);
}

/* 最近使ったアイコン */
.recent-icons-section {
  margin-bottom: var(--oh3-spacing-2);
  padding-bottom: var(--oh3-spacing-2);
  border-bottom: 1px dashed var(--oh3-color-border-default);
}

.recent-icons-label {
  font-size: var(--oh3-font-size-xs);
  color: var(--oh3-color-text-secondary);
  margin-bottom: 4px;
}

.recent-icons-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.recent-icons-grid .icon-grid-btn {
  width: 28px;
  height: 28px;
  padding: 4px;
}

.recent-icons-grid .icon-svg {
  width: 20px;
  height: 20px;
}

.icon-category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: var(--oh3-spacing-2);
}

.icon-category-tab {
  padding: 4px 8px;
  font-size: 11px;
  border: 1px solid var(--oh3-color-border-default);
  border-radius: var(--oh3-radius-sm);
  background: white;
  cursor: pointer;
  transition: all 0.15s ease;
}

.icon-category-tab:hover {
  background: var(--oh3-color-surface-alt);
}

.icon-category-tab.active {
  background: var(--oh3-color-primary);
  color: white;
  border-color: var(--oh3-color-primary);
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
}

.icon-grid-scrollable {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 4px;
}

.icon-category-tab-search,
.icon-category-tab-ai {
  display: flex;
  align-items: center;
  gap: 2px;
}

.mdi-link {
  display: flex;
  align-items: center;
  padding: 2px 4px;
  color: var(--oh3-color-text-muted);
  text-decoration: none;
  opacity: 0.6;
  transition: opacity 0.2s;
}
.mdi-link:hover {
  opacity: 1;
  color: var(--oh3-color-primary);
}

.icon-grid-btn {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid var(--oh3-color-border-default);
  border-radius: var(--oh3-radius-sm);
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 4px;
}

.icon-grid-btn:hover {
  transform: scale(1.1);
  box-shadow: var(--oh3-shadow-md);
  z-index: 1;
}

.icon-svg {
  width: 24px;
  height: 24px;
}

.icon-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: var(--oh3-font-size-sm);
  font-weight: 700;
  border: 2px solid;
  border-radius: var(--oh3-radius-full);
  background: white;
}

</style>
